import dayjs, { Dayjs } from 'dayjs';
import { useMemo } from 'react';

import { useGetPatientQuery } from '../api/patientApi';

export interface PatientDates {
  signupDate: Dayjs  | null;
  activationDate: Dayjs | null;
  preOpDate: Dayjs  | null;
  surgeryDate: Dayjs  | null;
  dischargeDate: Dayjs  | null;
  completedDate: Dayjs  | null;
  nowInHospitalTz: Dayjs | null;
  startOfDayInHospitalTz: Dayjs | null;
}

export interface PatientDatesInMS {
  signupDate: number | null;
  activationDate: number | null;
  preOpDate: number | null;
  surgeryDate: number | null;
  dischargeDate: number | null;
  completedDate: number | null;
}

function getTimezoneOffsetHours(timezone: string, dateInMs: number): number {
  try {
    const date = new Date(dateInMs);
    
    // Hospital datetime format
    const localFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      hour12: false
    });

    // UTC datetime format
    const utcFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      hour: 'numeric',
      hour12: false 
    });
    
    const localHours = parseInt(localFormatter.format(date), 10);
    const utcHours = parseInt(utcFormatter.format(date), 10);

    // Calculate the offset in hours
    let offsetHours = localHours - utcHours;
    
    // Adjust for day boundaries
    if (offsetHours > 12) offsetHours -= 24;
    if (offsetHours < -12) offsetHours += 24;
    
    return offsetHours;
  } catch (error) {
    console.error("Error calculating timezone offset:", error);
    return 0;
  }
}

// fabricates a fake timestamp whose displayed value always matches the hospital's
// local clock, regardless of the device's own timezone. This is NOT a real instant —
// only compare it against other values from dateInHospitalTz
export function dateInHospitalTz(dateInMs: number | null, timezone: string) {
  if (!dateInMs) return null;

  // Calculate timezone offset in minutes
  const offsetHours = getTimezoneOffsetHours(timezone, dateInMs);

  // Create a UTC dayjs object from the timestamp
  const utcDate = dayjs.utc(dateInMs);

  // Apply the timezone offset to get datetime in hospital timezone
  const hospitalZoneDate = utcDate.add(offsetHours, 'hour');
  return dayjs(hospitalZoneDate);
}

// 'now' in that same fabricated frame, safe to diff against patientDates.* values
// above — but it's still not a real instant, so never compare it to a real timestamp
export function getNowInHospitalTz(timezone: string): Dayjs {
  return dateInHospitalTz(Date.now(), timezone) as Dayjs;
}

export default function usePatientDates(options = { skip: false }) {
  const {
    data,
    error,
  } = useGetPatientQuery(undefined, { 
    refetchOnMountOrArgChange: 3600,
    skip: options.skip
  });

  const patientDatesInMS = useMemo(() => {
    if (!data || error) return null;

    const {
      createdAt,
      activationDate,
      preOpDate,
      surgeryDate,
      dischargeDate,
      completedDate,
    } = data;

    return {
      signupDate: createdAt || null,
      activationDate: activationDate || null,
      preOpDate: preOpDate || null,
      surgeryDate: surgeryDate || null,
      dischargeDate: dischargeDate || null,
      completedDate: completedDate || null,
    };
  }, [data, error]);

  const patientDates = useMemo(() => {
    if (!data || error) return null;

    const {
      createdAt,
      activationDate,
      preOpDate,
      surgeryDate,
      dischargeDate,
      completedDate,
      timezone,
    } = data;

    const nowInHospitalTz = getNowInHospitalTz(timezone);

    const dates = {
      signupDate: dateInHospitalTz(createdAt, timezone),
      activationDate: dateInHospitalTz(activationDate, timezone),
      preOpDate: dateInHospitalTz(preOpDate, timezone),
      surgeryDate: dateInHospitalTz(surgeryDate, timezone),
      dischargeDate: dateInHospitalTz(dischargeDate, timezone),
      completedDate: dateInHospitalTz(completedDate, timezone),
      nowInHospitalTz,
      startOfDayInHospitalTz: nowInHospitalTz.startOf('day'),
    };

    return dates;
  }, [data, error]);

  const surgeryDate = useMemo(() => {
    if (!patientDates) return null;
    return patientDates.surgeryDate;
  }, [patientDates]);

  const dischargeDate = useMemo(() => {
    if (!patientDates) return null;
    return patientDates.dischargeDate;
  }, [patientDates]);

  const sameTimeZone = useMemo(() => {
    if (!data || error) return null;

    const { timezone } = data;
    const hospitalZone = dayjs().tz(timezone).format('z');
    const userZone = dayjs().tz(dayjs.tz.guess()).format('z');

    return hospitalZone === userZone;
  }, [data, error]);

  return {
    patientDates,
    patientDatesInMS,
    surgeryDate,
    dischargeDate,
    sameTimeZone,
  };
}